<script lang="ts">
	import {
	    FoodResultType,
	    SearchView,
	    type FoodResults
	} from "$lib/def/landing";
	import { profile$ } from "$lib/serv/core/auth";
	import { fetch$ } from "$lib/serv/core/cache";
	import { SEARCH_PROPKEYS, getDB } from "$lib/serv/food-landing";
	import { Vertical } from "$lib/def/verticals";
	import Illustration from "$lib/ui/layout/Illustration.svelte";
	import { objectPropsEqual } from "$lib/util/object";
	import {
	    BehaviorSubject,
	    EMPTY,
	    combineLatest,
	    combineLatestWith,
	    distinctUntilChanged,
	    distinctUntilKeyChanged,
	    filter,
	    map,
	    of,
	    shareReplay,
	    startWith,
	    switchMap,
	} from "rxjs";
	import { baseLandingRestaurant$, baseLandingShop$ } from "../FoodLanding.svelte";
	import RestaurantGroups from "./search/RestaurantGroups.svelte";
	import Restaurants from "./search/Restaurants.svelte";
	import Tags from "./search/Tags.svelte";

	/**
	 * One way this component can be sped up is to isolate loading base and semi-static
	 * only (excluding dynamic and deals) before performing search queries. We only need
	 * the restaurant IDs for search.
	 * 
	 * UPDATE: because of `distinctUntilKeyChanged('loading')` the loading states of
	 * dynamic data was ignored. So `baseLanding[2]?.loading` also needs to be checked.
	 */
	
	export let urlSearchParams: Record<string, string>;

	const subjPrams = new BehaviorSubject(urlSearchParams);
	const param$ = subjPrams.asObservable();
	$: subjPrams.next(urlSearchParams);
	
	const retrySearch = new BehaviorSubject<typeof EMPTY>(EMPTY);

	// Step 1: prepare the landing items stream
	const landingitems$ = param$.pipe(
		map(p => ({
			search_vertical: p.search_vertical || p.vertical,
		})),
		distinctUntilKeyChanged('search_vertical'),
		switchMap(({search_vertical}) => of(search_vertical).pipe(
			switchMap(v => {
				if (v === Vertical.Store) return baseLandingShop$;
				// if (v === Vertical.Restaurant)
				return baseLandingRestaurant$;
			}),
			map(baseLanding => ({
				baseLanding,
				search_vertical,
				// combine semi-static and dynamic loading status
				loading: !baseLanding[0] || baseLanding[0]?.loading || !baseLanding[1] || baseLanding[1]?.loading || !baseLanding[2] || baseLanding[2]?.loading,
			})),
			startWith({
				baseLanding: [null, null, null, null],
				search_vertical,
				loading: true,
			}),
		)),
		shareReplay(1), // reduces unsub-sub due to the switchMap and we subscribe twice below
	);

	// Step 2: create a stream that emits every time LI is ready
	const search$ = landingitems$.pipe(
		distinctUntilKeyChanged('loading'), // reduce noise
		filter(({loading}) => !loading), // only when LI is done loading
		switchMap(({baseLanding, search_vertical, loading}) => param$.pipe(
			// filter(p => search_vertical === p.search_vertical), // realign emits
			map(p => ({
				search_vertical,
				search: p.search,
				latitude: p.latitude,
				longitude: p.longitude,
			})),
			distinctUntilChanged(objectPropsEqual('search_vertical', 'search', 'latitude', 'longitude')),
			filter(({ search }) => search !== null),
			map(p => ({
				text: p.search,
				lat: p.latitude,
				lon: p.longitude,
				search_vertical,
				baseLanding,
			})),
		)),
	)

	// Step 3: create the data stream which translates a search$ emit into a net request
	// it combines profile$ for ML purpose and a retry stream
	const results$ = combineLatest([search$, profile$, retrySearch]).pipe(
		switchMap(([{ baseLanding: [base, ss, dyn, br, deals], text, search_vertical, lat, lon}, user]) => {
			let url = `api://search/`;
			if (search_vertical) url += `?vertical=${search_vertical}`;
			return fetch$<FoodResults>(new URL(url), SEARCH_PROPKEYS, { db: getDB(), storeName: 'search', msMaxCacheAge: 1000 * 30 }, {
				text,
				meta: {
					lat,
					lon,
					user_id: user?.id ?? undefined,
				},
				ids: ss?.payload?.semi_static.map(r => r.id).sort() || [],
			}).pipe(
				combineLatestWith(landingitems$), // could possibly be left out
				map(([cache, {loading}]) => ({base, ss, dyn, br, deals, text, cache, loading})),
			);
		}),
	);

</script>
{#if !$results$ || $results$.cache.loading}

	<!-- <Grid cache={$results$.cache} type="filters">
		<div slot="title"></div>
		<h3>Hello?</h3>
	</Grid>
	<Carousel cache={$results$.cache} type="brands">
		<div slot="title"></div>
		<h3>Hello?</h3>
	</Carousel>
	<Grid cache={$results$.cache} type="restaurants">
		<div slot="title"></div>
		<h3>Hello?</h3>
	</Grid> -->

<h3 class="headline-m bold loading">&nbsp;</h3>
<div class="grid filters loading">
	{#each Array(6) as _}
	<a href={null} class="item">
		<div class="image circle"></div>
		<div class="text"></div>
	</a>
	{/each}
</div>

<h3 class="headline-m bold loading">&nbsp;</h3>
<div class="grid restaurants loading">
	{#each Array(6) as _}
	<a href={null} class="item">
		<div class="image"></div>
		<div class="text"></div>
	</a>
	{/each}
</div>

{:else if $results$}
	{@const {ss, dyn, br, deals, text, cache} = $results$}
	{@const semistatic = ss?.payload?.semi_static ?? []}
	{@const dynamic = dyn?.payload?.dynamic ?? []}
	{@const brands = br?.payload?.brand_logos ?? []}

	{#if cache.error}
		<div class="flex mobile align-center justify-start">
			<span style="max-width: 150px;">
				<Illustration src='general-error-banana-medium' />
			</span>
			<div style="text-align: left;">
				<div class="headline-s bold book grey">Error searching restaurants</div>
				<div class="body-m book grey">Try searching for something else.</div>
				<button class="body-m bold" on:click={() => retrySearch.next(EMPTY)}><span class="icon-refresh-20"></span>&nbsp; Retry</button>
			</div>
		</div>
	{:else if cache.payload}
		
		{#if cache.payload?.view === SearchView.NoSearchResult}
			<div class="flex mobile align-center justify-start">
				<span style="max-width: 150px;">
					<Illustration src='general-error-banana-medium' />
				</span>
        <article>
          <div class="headline-s bold book grey" style="text-align: left;">We couldn't find anything for "{urlSearchParams.search}"...</div>
          <div class="headline-s bold book grey" style="text-align: left;">
            Try the toggle at the bottom to search {!urlSearchParams.search_vertical || urlSearchParams.search_vertical === Vertical.Restaurant ? 'dishes' : 'items'}.
          </div>
        </article>
			</div>
		{/if}

		{#each cache.payload.items as group}
			{#each group.tiles as tile}
				{#if tile.payload.item_type === FoodResultType.RecommendedTag}
					<Tags searchParams={urlSearchParams} payload={tile.payload} recommended={true} />
				{:else if tile.payload.item_type === FoodResultType.Tag}
					<Tags searchParams={urlSearchParams} payload={tile.payload} />
				{:else if tile.payload.item_type === FoodResultType.Restaurant || tile.payload.item_type === FoodResultType.RecommendedRestaurant}
					<Restaurants searchParams={urlSearchParams} payload={tile.payload} {semistatic} {dynamic} {deals} />
				{:else if tile.payload.item_type === FoodResultType.RecommendedRestaurantGroup}
					<RestaurantGroups searchParams={urlSearchParams} payload={tile.payload} {brands} />
				{/if}
			{/each}
		{/each}
	{/if}

{/if}

<style lang="scss">
  @use "screen";

  span {
    &.desktop {
      @media (screen.$max-md) {
        display: none;
      }
    }
    &.mobile {
      @media (screen.$md) {
        display: none;
      }
    }
  }
</style>
